import { useEffect, useRef, useState } from 'react';
import { api, ApiError, type InvoiceLine, type NewProduct, type Product, type VatRateCode } from '../api';
import type { Dictionary, Locale } from '../i18n';
import { amount } from '../format';
import * as Icon from './Icons';

const rates: VatRateCode[] = ['23', '8', '5', '0', 'zw', 'np', 'oo'];

export function ProductDialog({ companyId, line, netPrice, t, locale, onCreated, onClose }: {
  companyId: string;
  line: InvoiceLine;
  netPrice: string;
  t: Dictionary;
  locale: Locale;
  onCreated: (product: Product) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [form, setForm] = useState<NewProduct>({
    code: null, names: { pl: line.name }, unit: line.unit, vatRate: line.vatRate,
    gtuCode: line.gtuCode ?? null, pkwiu: line.pkwiu ?? null, cn: null,
    defaultPrice: netPrice, currency: 'PLN', active: true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    ref.current?.showModal();
  }, []);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onCreated(await api.addProduct(companyId, {
        ...form,
        code: form.code?.trim() || null,
        gtuCode: form.gtuCode?.trim() || null,
        defaultPrice: form.defaultPrice?.trim().replace(',', '.') || null,
        names: Object.fromEntries(Object.entries(form.names).filter(([, value]) => value.trim())
          .map(([language, value]) => [language, value.trim()])),
      }));
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <dialog className="dlg" ref={ref} onClose={onClose} aria-label={t.products.add}>
      <form className="panel" onSubmit={save}>
        <div className="hd"><h4>{t.products.add}</h4></div>
        {error && <div className="alert a-bad"><Icon.Error /><b>{error}</b></div>}
        <div className="fields">
          <div className="field">
            <label htmlFor="invoiceProductName">{t.products.namePl}</label>
            <input id="invoiceProductName" className="inp" value={form.names.pl}
              onChange={(event) => setForm({ ...form, names: { pl: event.target.value } })} required />
          </div>
          <div className="field">
            <label htmlFor="invoiceProductCode">{t.products.code}</label>
            <input id="invoiceProductCode" className="inp" value={form.code ?? ''}
              onChange={(event) => setForm({ ...form, code: event.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="invoiceProductNameEn">{t.products.nameEn}</label>
            <input id="invoiceProductNameEn" className="inp" value={form.names.en ?? ''}
              onChange={(event) => setForm({ ...form, names: { ...form.names, en: event.target.value } })} />
          </div>
          <div className="field">
            <label htmlFor="invoiceProductUnit">{t.products.unit}</label>
            <input id="invoiceProductUnit" className="inp" value={form.unit}
              onChange={(event) => setForm({ ...form, unit: event.target.value })} required />
          </div>
          <div className="field">
            <label htmlFor="invoiceProductVat">{t.products.vatRate}</label>
            <select id="invoiceProductVat" className="inp" value={form.vatRate}
              onChange={(event) => setForm({ ...form, vatRate: event.target.value as VatRateCode })}>
              {rates.map((rate) => <option key={rate} value={rate}>{t.products.rates[rate]}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="invoiceProductPrice">{t.invoices.netUnitPrice}</label>
            <input id="invoiceProductPrice" className="inp num" inputMode="decimal"
              value={form.defaultPrice ?? ''}
              onChange={(event) => setForm({ ...form, defaultPrice: event.target.value })} />
            <span className="hint">{amount(form.defaultPrice?.replace(',', '.'), locale)}</span>
          </div>
          <div className="field">
            <label htmlFor="invoiceProductGtu">{t.products.gtu}</label>
            <input id="invoiceProductGtu" className="inp mono" value={form.gtuCode ?? ''}
              onChange={(event) => setForm({ ...form, gtuCode: event.target.value })} />
          </div>
        </div>
        <div className="bar2">
          <button type="submit" className="btn" disabled={busy || !form.names.pl.trim()}>
            {busy ? t.companies.saving : t.products.save}
          </button>
          <div className="sp"><button type="button" className="btn ghost" disabled={busy}
            onClick={() => ref.current?.close()}>{t.companies.cancel}</button></div>
        </div>
      </form>
    </dialog>
  );
}
